import { PropsWithChildren, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';

import { BoxedStyling, ReadOnlyStyling, ToastProvider } from '@treams/treams-ui';

import './app.css';
import ConfigGuard from './packages/kakapo-ui-application/containers/ConfigGuard';
import FullPageLoadingContainer from './packages/kakapo-ui-application/pages/loading/FullPageLoadingContainer';
import {
	ApolloProvider,
	BaseThemeProvider,
	StyleInjectionProvider,
	WorkspaceProvider,
	WorkspaceThemeProvider,
	withContextProviders,
} from './plugins';
import { initializePlugins } from './plugins';

if (import.meta.env.DEV) {
	void import('@locator/runtime')
		.then(({ default: setupLocatorUI }) => setupLocatorUI())
		.catch((error) => console.warn('LocatorJS failed to load', error));
}

initializePlugins();

try {
	if (sessionStorage.getItem('original-path')?.toLowerCase().startsWith('/sso')) {
		sessionStorage.removeItem('original-path');
	}

	const storedPath = sessionStorage.getItem('original-path');
	const originalPath =
		`${window.location.pathname + window.location.search + window.location.hash}`.toLowerCase();

	if (
		!storedPath &&
		!originalPath.startsWith('/authentication') &&
		!originalPath.startsWith('/sso') &&
		!originalPath.startsWith('/logout') &&
		originalPath !== '/'
	) {
		sessionStorage.setItem('original-path', originalPath);
	}
} catch {
	// sessionStorage can throw in private mode or when storage is disabled
}

/* Component definition */
const SuspenseProvider = ({ children }: PropsWithChildren) => {
	return <Suspense fallback={<FullPageLoadingContainer />}>{children}</Suspense>;
};

const providers: React.ComponentType[] = [
	// Outermost so even the Suspense fallback injects into the ordered containers.
	StyleInjectionProvider,
	BaseThemeProvider,
	SuspenseProvider,
	BrowserRouter,
	WorkspaceProvider,
	ApolloProvider,
	ToastProvider,
	WorkspaceThemeProvider,
];

/**
 * Base things that need to be wrapped
 * @returns Base component
 */
const Base = () => {
	return (
		<>
			<BoxedStyling />
			<ReadOnlyStyling />
			<ConfigGuard />
		</>
	);
};

export const Root = withContextProviders(providers)(Base);

createRoot(document.getElementById('root')!).render(<Root />);

export default Root;
